import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { exportReservationsToCSV } from "@/utils/csvExport";
import { DataTable } from "@/components/ui/data-table";
import { useReservationFiltering } from "@/hooks/useReservationFiltering";
import { Ban, MessageSquare, Plus, Upload, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ReservationNavigation } from "./components/ReservationNavigation";
import ReservationStats from "./components/ReservationStats";
import ReservationFilters from "./components/ReservationFilters";
import { getReservationColumns } from "./utils/getReservationColumns";
import { useBulkCancelReservations } from "@/hooks/useReservations";
import { useCreateChannelWithGuest } from "@/components/pages/chats/hooks/useChannel";
import { toast } from "sonner";

export const MainReservationsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const bulkCancelReservations = useBulkCancelReservations();
  const createChannelWithGuest = useCreateChannelWithGuest();

  const {
    reservations,
    isLoading,
    error,
    filters,
    actions,
    pagination,
    selection,
  } = useReservationFiltering();

  const columns = getReservationColumns(selection, {
    selection: true,
    status: true,
    room: true,
    actions: true,
  });

  const hasSelection = selection.selectedCount > 0;
  const isBulkActionPending =
    bulkCancelReservations.isPending || createChannelWithGuest.isPending;

  const handleExport = () => {
    const dataToExport = hasSelection
      ? reservations.filter((reservation) =>
          selection.selectedReservations.includes(reservation.id),
        )
      : reservations;

    exportReservationsToCSV(
      dataToExport,
      `reservations_${new Date().toISOString().split("T")[0]}.csv`,
    );
  };

  const handleCancelSelectedReservations = async () => {
    const selectedIds = selection.selectedReservations;
    if (
      selectedIds.length === 0 ||
      !window.confirm(
        t(
          "reservation.bulkActions.cancelReservationConfirm",
          "Cancel selected reservations?",
        ),
      )
    ) {
      return;
    }

    await bulkCancelReservations.mutateAsync(selectedIds);
    toast.success(
      t(
        "reservation.bulkActions.cancelReservationSuccess",
        "Selected reservations cancelled",
      ),
    );
    selection.clearSelection();
  };

  const handleSendMessageToSelectedReservation = async () => {
    const firstSelectedReservationId = selection.selectedReservations[0];
    if (!firstSelectedReservationId) {
      return;
    }

    const channel = await createChannelWithGuest.mutateAsync(
      firstSelectedReservationId,
    );
    if (!channel.id) {
      return;
    }

    navigate(`/chats?activeChannel=${encodeURIComponent(channel.id)}`);
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-gray-500">Loading reservations...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-red-500">Error loading reservations</div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-gray-50 p-6">
      <ReservationNavigation activeTab="reservations" />

      <ReservationStats dateRange={filters.dateRange} />

      <div className="mt-6 mb-6 flex items-center gap-4">
        {hasSelection ? (
          <span className="text-sm font-medium text-neutral-400">
            {t("reservation.table.selectedCount", "{{count}} selected", {
              count: selection.selectedCount,
            })}
          </span>
        ) : (
          <ReservationFilters />
        )}

        <div className="ml-auto flex items-center gap-2">
          {!hasSelection && (
            <Button
              variant="new_default"
              onClick={() => navigate("/reservations/add")}
            >
              <Plus />
              Add reservation
            </Button>
          )}
          <Button variant="new_outline" onClick={handleExport}>
            <Upload />
            {t("reservation.buttons.export", "Export")}
          </Button>
          {hasSelection && (
            <>
              <Button
                variant="new_destructive"
                onClick={handleCancelSelectedReservations}
                disabled={isBulkActionPending}
              >
                <Ban />
                {t(
                  "reservation.bulkActions.cancelReservation",
                  "Cancel reservation",
                )}
              </Button>
              <Button
                variant="new_outline"
                onClick={handleSendMessageToSelectedReservation}
                disabled={isBulkActionPending}
              >
                <MessageSquare />
                {t("reservation.buttons.sendMessage", "Send message")}
              </Button>
              <Button
                variant="new_outline"
                onClick={selection.clearSelection}
                disabled={isBulkActionPending}
              >
                <X />
                {t(
                  "reservation.bulkActions.cancelSelection",
                  "Cancel selection",
                )}
              </Button>
            </>
          )}
        </div>
      </div>

      <DataTable
        data={reservations}
        columns={columns}
        loading={isLoading}
        onRowClick={(reservation) =>
          navigate(`/reservations/${reservation.id}`)
        }
        pagination={{
          currentPage: pagination.currentPage,
          totalPages: pagination.totalPages,
          onPageChange: pagination.goToPage,
        }}
        sorting={{
          field: filters.sortField,
          direction: filters.sortDirection,
          onSort: actions.handleSort,
        }}
      />
    </div>
  );
};
